import { useEffect, useState } from "react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { StatCard } from "./components/stat-card"
import { ActivityFeed } from "./components/activity-feed"
import {
  ChildrenIcon,
  SponsorIcon,
  GivingIcon,
  NominationsIcon,
  SchoolsIcon,
} from "./components/icons"

interface OverviewData {
  stats: {
    childrenSponsored: number | null
    awaitingSponsor: number | null
    activeDonors: number | null
    raisedThisQuarter: number | null
    completedGiftsThisQuarter: number | null
    pendingNominations: number | null
    nominationsUnderReview: number | null
    newSchoolApplications: number | null
  }
  charts: {
    giving: { month: string; amount: number }[] | null
    sponsorship: { month: string; sponsored: number; pending: number }[] | null
  }
}

interface ActivityItem {
  id: string
  title: string
  body: string | null
  actorName: string | null
  timestamp: string
  href?: string
}

const formatNaira = (value: number) => {
  if (value >= 1000000) {
    return `N${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `N${(value / 1000).toFixed(0)}K`
  }
  return `N${value}`
}

function formatRelativeDate(value: string): string {
  const elapsed = Date.now() - new Date(value).getTime()
  const minutes = Math.floor(elapsed / 60000)
  if (minutes < 1) return "Just now"
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`
  return new Date(value).toLocaleDateString("en-GB")
}

export function OverviewPage() {
  const [overview, setOverview] = useState<OverviewData | null>(null)
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoading(true)
      setError(null)
      setActivity([])

      try {
        const [overviewResult, activitiesResult] = await Promise.allSettled([
          client.api.admin.overview.$get(),
          client.api.admin.activities.$get({ query: { limit: "8", offset: "0" } }),
        ])
        if (overviewResult.status === "rejected") throw overviewResult.reason

        const overviewResponse = overviewResult.value
        const overviewBody = await overviewResponse.json()

        if (!overviewResponse.ok) {
          throw new Error(getErrorMessage(overviewBody))
        }

        if (!cancelled && "data" in overviewBody) {
          setOverview(overviewBody.data)
        }

        if (!cancelled && activitiesResult.status === "fulfilled" && activitiesResult.value.ok) {
          const activitiesBody = await activitiesResult.value.json()
          if ("data" in activitiesBody) {
            setActivity(
              activitiesBody.data.items.map((item) => ({
                id: item.id,
                title: item.title,
                body: item.body,
                actorName: item.actorName,
                timestamp: formatRelativeDate(item.createdAt),
                href: item.subjects.find((subject) => subject.href)?.href ?? undefined,
              })),
            )
          }
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : "Failed to load overview")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  const statCards = overview
    ? [
        {
          title: "Children Sponsored",
          value: overview.stats.childrenSponsored,
          subtitle:
            overview.stats.awaitingSponsor === null
              ? "Current sponsored children"
              : `${overview.stats.awaitingSponsor} awaiting sponsorship`,
          icon: <ChildrenIcon className="h-5 w-5" />,
        },
        {
          title: "Awaiting Sponsor",
          value: overview.stats.awaitingSponsor,
          subtitle: "Verified children",
          icon: <SponsorIcon className="h-5 w-5" />,
        },
        {
          title: "Active Donors",
          value: overview.stats.activeDonors,
          subtitle: "Current active sponsors",
          icon: <SponsorIcon className="h-5 w-5" />,
        },
        {
          title: "Raised This Quarter",
          value: overview.stats.raisedThisQuarter === null ? null : formatNaira(overview.stats.raisedThisQuarter),
          subtitle:
            overview.stats.completedGiftsThisQuarter === null
              ? "Completed giving"
              : `${overview.stats.completedGiftsThisQuarter} completed gifts`,
          icon: <GivingIcon className="h-5 w-5" />,
        },
        {
          title: "Pending Nominations",
          value: overview.stats.pendingNominations,
          subtitle:
            overview.stats.nominationsUnderReview === null
              ? "Awaiting review"
              : `${overview.stats.nominationsUnderReview} under review`,
          icon: <NominationsIcon className="h-5 w-5" />,
        },
        {
          title: "New School Applications",
          value: overview.stats.newSchoolApplications,
          subtitle: "Awaiting partnership review",
          icon: <SchoolsIcon className="h-5 w-5" />,
        },
      ]
    : []

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">
          Overview
        </h1>
        <p className="mt-1 text-sm text-ink/60">
          Welcome back. Here's what's happening with your organization.
        </p>
      </div>

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-5 text-sm text-red-700">
          <div className="flex items-center justify-between gap-4">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setReloadKey((value) => value + 1)}
              className="font-medium underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="h-32 animate-pulse rounded-[10px] border border-gold-500 bg-white" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {statCards.map((stat, index) => {
            if (stat.value === null) return null
            return (
              <div
                key={stat.title}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <StatCard
                  title={stat.title}
                  value={stat.value}
                  subtitle={stat.subtitle}
                  trend="neutral"
                  icon={stat.icon}
                />
              </div>
            )
          })}
        </div>
      )}

      {loading ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="h-80 animate-pulse rounded-[10px] border border-gold-500 bg-white" />
          <div className="h-80 animate-pulse rounded-[10px] border border-gold-500 bg-white" />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {overview?.charts.giving && (
            <div className="animate-fade-in-up rounded-[10px] border border-gold-500 bg-white p-5 transition-all duration-200 hover:border-forest-500 hover:shadow-md" style={{ animationDelay: "300ms" }}>
              <h3 className="font-display text-lg font-semibold text-ink">
                Giving - Last 6 Months
              </h3>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview.charts.giving} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8d9a8" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#1a1a1a", fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#1a1a1a", fontSize: 12 }}
                  tickFormatter={formatNaira}
                  width={60}
                />
                <Tooltip
                  formatter={(value) => [formatNaira(Number(value)), "Amount"]}
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e8d9a8",
                    borderRadius: "8px",
                    fontSize: "12px"
                  }}
                />
                <Bar
                  dataKey="amount"
                  fill="#0a3a2a"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={40}
                />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {overview?.charts.sponsorship && (
            <div className="animate-fade-in-up rounded-[10px] border border-gold-500 bg-white p-5 transition-all duration-200 hover:border-forest-500 hover:shadow-md" style={{ animationDelay: "350ms" }}>
              <h3 className="font-display text-lg font-semibold text-ink">
                Sponsorship by Date
              </h3>
              <div className="mt-4 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={overview.charts.sponsorship} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8d9a8" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#1a1a1a", fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#1a1a1a", fontSize: 12 }}
                  width={30}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "white",
                    border: "1px solid #e8d9a8",
                    borderRadius: "8px",
                    fontSize: "12px"
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="sponsored"
                  stackId="1"
                  stroke="#0a3a2a"
                  fill="#0a3a2a"
                  fillOpacity={0.8}
                  name="Sponsored"
                />
                <Area
                  type="monotone"
                  dataKey="pending"
                  stackId="1"
                  stroke="#e8d9a8"
                  fill="#e8d9a8"
                  fillOpacity={0.8}
                  name="Pending"
                />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-3 flex items-center justify-center gap-6 text-xs">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm bg-forest-950" />
                  <span className="text-ink/60">Sponsored</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm bg-gold-500" />
                  <span className="text-ink/60">Pending</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && <ActivityFeed items={activity} />}
    </div>
  )
}
